import { ChangeDetectionStrategy, Component, ElementRef, ViewEncapsulation, computed, inject, input, output, signal } from '@angular/core';
import { Highlightable } from '@angular/cdk/a11y';
import { ContentComponent, ContentModel } from '@workbuddy/ui/v2/components/primitives/content';

@Component({
  selector: 'wb-picker-item',
  standalone: true,
  imports: [ContentComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./button.picker-item.component.less'],
  template: `
    <button class="sp-picker-item" [class.active]="$highlighted()" [attr.id]="$elementId()" tabindex="-1" (click)="toggle.emit()" [wbContent]="effectiveContent()"></button>
  `,
})
export class PickerItemComponent implements Highlightable {

  public content = input.required<ContentModel>({ alias: 'content' });
  public checked = input(false, { alias: 'checked' });

  protected readonly $highlighted = signal(false);
  protected readonly $elementId = computed(() => `wb-picker-item-${this.uid}`);

  private static _nextUid = 0;
  private readonly uid = ++PickerItemComponent._nextUid;
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  protected effectiveContent = computed<ContentModel>(() => ({
    ...this.content(),
    checkable: this.content().checkable ?? 'none',
    checked: this.checked(),
  }));

  public toggle = output<void>();

  // ── ActiveDescendantKeyManager / Highlightable ──

  public setActiveStyles(): void {

    this.$highlighted.set(true);
    this.scrollIntoView();
  }

  public setInactiveStyles(): void {

    this.$highlighted.set(false);
  }

  /** Used by `withTypeAhead()` to match keystrokes — unused for active-descendant filtering. */
  public getLabel(): string {

    return this.content()?.label?.toString() ?? '';
  }

  /** DOM id of the inner button — the search input points at this via aria-activedescendant. */
  public getId(): string {

    return this.$elementId();
  }

  private scrollIntoView(): void {

    const el = this.host.nativeElement.querySelector<HTMLElement>('.sp-picker-item');
    if (!el) return;

    const scrollRoot = el.closest<HTMLElement>('.sp-picker-list-root');
    if (!scrollRoot) {
      el.scrollIntoView({ block: 'nearest' });
      return;
    }

    const rootRect = scrollRoot.getBoundingClientRect();
    const itemRect = el.getBoundingClientRect();
    const padding = 4;
    const topBoundary = this.getVisibleTopBoundary(scrollRoot, rootRect);

    if (itemRect.top < topBoundary) {
      scrollRoot.scrollTop -= topBoundary - itemRect.top + padding;
      return;
    }

    if (itemRect.bottom > rootRect.bottom) {
      scrollRoot.scrollTop += itemRect.bottom - rootRect.bottom + padding;
    }
  }

  private getVisibleTopBoundary(scrollRoot: HTMLElement, rootRect: DOMRect): number {

    const headers = Array.from(scrollRoot.querySelectorAll<HTMLElement>('.sp-picker-section-header, .sp-picker-group-header'));
    return headers.reduce((boundary, header) => {
      const rect = header.getBoundingClientRect();
      const overlapsTop = rect.top <= boundary && rect.bottom > rootRect.top;
      return overlapsTop ? Math.max(boundary, rect.bottom) : boundary;
    }, rootRect.top);
  }
}
